import { MaintenanceStatus, UserRepo, UserType } from 'user/User';
import { API } from 'utils/api';
import { bodyMessageOf, messageOf, statusOf } from 'utils/apiError';
import { injector } from 'Injector';
import { STORAGE_INTERACTOR_KEY, StorageInteractor } from 'utils/storageInteractor';


import { Notification, NotificationTypes } from 'components/common/Notification/Notification';
import { STATUS_NOT_FOUND, STATUS_SERVER_ERROR, STATUS_BACKEND_ERROR } from '../../utils/constants';
import { School } from 'distribution/Distribution';
export interface UserDTO {
  role: UserType;
  name: string;
  id: number;
  photo: string;
  email?: string;
  schools: Array<School>;
  isSuperCM: boolean;
  teacherTrial: boolean;
  isTestAccount?: boolean;
  signature?: string;
}

export interface StudentDTO {
  grade: string;
}

/**
 * `access_token` is `'login_wp'` rather than a token when the backend wants the browser
 * redirected to WordPress SSO instead, which is why `getUserWithTokenFromLogPass` compares
 * it to a string literal before storing it.
 */
interface LoginResponseDTO {
  access_token: string;
  user: object;
  sso?: string;
}

interface LogoutResponseDTO {
  post_logout_redirect_uri: string;
  id_token: string;
}

export class UserApi implements UserRepo {
  private storageInteractor = injector.get<StorageInteractor>(STORAGE_INTERACTOR_KEY);

  /**
   * The two WordPress endpoints LoginStore used to call through `API` directly. They live
   * here now for the same reason every other endpoint does: the repo is the seam the test
   * harness fakes, and a store that reaches past it cannot be tested without the network.
   */
  public async getMaintenanceStatus(): Promise<MaintenanceStatus | false> {
    const response = await API.get<MaintenanceStatus>(`${process.env.REACT_APP_WP_API_URL}/wp-json/maintenance/v1/status`);

    if (!response || !response.data) {
      return false;
    }

    return response.data;
  }

  public async getMenuData(locale: string): Promise<Array<unknown>> {
    const response = await API.get<Array<unknown>>(`${process.env.REACT_APP_WP_API_URL}/wp-json/menu_list/v1/get`, {
      params: { lang: locale },
    });

    if (!response || !response.data) {
      return [];
    }

    return response.data;
  }

  public async getUserWithToken(code: string): Promise<string | null> {
    try {
      const response = await API.post<LoginResponseDTO>('/api/login/dataporten/authorize', { code }, { withCredentials: true });
      this.storageInteractor.setUserWithToken(response.data.access_token, response.data.user);
      return response.data.access_token;
    } catch (error) {
      if (statusOf(error) === STATUS_NOT_FOUND || statusOf(error) === STATUS_SERVER_ERROR) {
        Notification.create({
          type: NotificationTypes.ERROR,
          title: bodyMessageOf(error) ?? messageOf(error)
        });
        return null;
      }
      if (statusOf(error) === STATUS_BACKEND_ERROR) {
        return null;
      }
      throw new Error(messageOf(error), { cause: error });
    }
  }

  public async getUserWithTokenFromLogPass(email: string, password: string) {
    try {
      const response = await API.post<LoginResponseDTO>('/api/login', { email, password }, { withCredentials: true });
      if (response.data.access_token === 'login_wp') {
        window.location.href = `${process.env.REACT_APP_WP_URL}/wp-content/plugins/skolerom-sso/skolerom-sso-login.php?nl=1&sso=${response.data.sso}`;
        return 'login_wp';
      }
      this.storageInteractor.setUserWithToken(response.data.access_token, response.data.user);
      return response.data.access_token;
    } catch (error) {
      Notification.create({
        type: NotificationTypes.ERROR,
        title: bodyMessageOf(error) ?? messageOf(error)
      });
    }
  }

  public async logOut() {
    const currentUserRole = this.storageInteractor.getUserRole();

    let data: LogoutResponseDTO;
    try {
      ({ data } = await API.get<LogoutResponseDTO>('/api/logout', { withCredentials: true }));
    } catch (error) {
      // Signed out here anyway, as a 401 does in utils/api: on a shared school computer, a
      // logout that did not happen is worse than one the server did not hear about (#224).
      // Without the response there is no id_token, so the Feide session is not ended, and the
      // next login may go straight through.
      console.error('Logout request failed; signing out locally:', messageOf(error));
      this.storageInteractor.logOut();
      window.location.href = '/login';
      return;
    }
    this.storageInteractor.logOut();

    if (currentUserRole === UserType.ContentManager) {
      window.location.href = `${process.env.REACT_APP_WP_URL}`;
    } else {
      window.location.href = 'https://auth.dataporten.no/openid/endsession?' +
      `post_logout_redirect_uri=${encodeURIComponent(data.post_logout_redirect_uri)}&id_token_hint=${data.id_token}`;

    }
  }
}
